import React, { useState, useEffect } from "react";
import {
    Paper,
    Grid,
    Typography,
    Box,
    Card,
    CardContent,
    CircularProgress,
    TextField,
    MenuItem,
    FormControl,
    InputLabel,
    Select
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { format, subDays } from "date-fns";
import {
    PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ResponsiveContainer
} from "recharts";
import * as marketingDashboardService from "../../services/marketingDashboard";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";

const useStyles = makeStyles((theme) => ({
    mainPaper: {
        flex: 1,
        padding: theme.spacing(2),
        overflowY: "scroll",
        ...theme.scrollbarStyles,
    },
    card: {
        height: "100%",
        display: "flex",
        flexDirection: "column",
    },
    cardContent: {
        flexGrow: 1,
    },
    metricCard: {
        padding: theme.spacing(2),
        textAlign: "center",
        backgroundColor: theme.palette.background.paper,
        borderRadius: theme.shape.borderRadius,
        boxShadow: theme.shadows[2],
    },
    metricValue: {
        fontSize: "2rem",
        fontWeight: "bold",
        color: theme.palette.primary.main,
    },
    metricLabel: {
        fontSize: "0.875rem",
        color: theme.palette.text.secondary,
        marginTop: theme.spacing(0.5),
    },
    filterBox: {
        marginBottom: theme.spacing(3),
        padding: theme.spacing(2),
        backgroundColor: "#f5f5f5",
        borderRadius: theme.shape.borderRadius,
    },
    funnelStep: {
        padding: theme.spacing(2),
        margin: theme.spacing(1, 0),
        backgroundColor: theme.palette.primary.main,
        color: "#fff",
        borderRadius: theme.shape.borderRadius,
        textAlign: "center",
        position: "relative",
    },
}));

const COLORS = {
    'Meta Ads': '#0084FF',
    'Google Ads': '#34A853',
    'Outras Origens': '#FF9800',
    'Não Rastreada': '#9E9E9E',
};

const MarketingDashboard = () => {
    const classes = useStyles();

    const [loading, setLoading] = useState(true);
    const [period, setPeriod] = useState("30");
    const [startDate, setStartDate] = useState(format(subDays(new Date(), 30), "yyyy-MM-dd"));
    const [endDate, setEndDate] = useState(format(new Date(), "yyyy-MM-dd"));

    const [overview, setOverview] = useState(null);
    const [sources, setSources] = useState([]);
    const [timeline, setTimeline] = useState([]);
    const [funnel, setFunnel] = useState(null);
    const [metrics, setMetrics] = useState(null);

    useEffect(() => {
        if (period !== "custom") {
            const days = parseInt(period);
            setStartDate(format(subDays(new Date(), days), "yyyy-MM-dd"));
            setEndDate(format(new Date(), "yyyy-MM-dd"));
        }
    }, [period]);

    useEffect(() => {
        fetchData();
    }, [startDate, endDate]);

    const fetchData = async () => {
        setLoading(true);
        try {
            const params = { startDate, endDate };
            console.log('[MarketingDashboard] Fetching data with params:', params);

            const [overviewData, sourcesData, timelineData, funnelData, metricsData] = await Promise.all([
                marketingDashboardService.getOverview(params),
                marketingDashboardService.getLeadsBySource(params),
                marketingDashboardService.getTimeline(params),
                marketingDashboardService.getSalesFunnel(params),
                marketingDashboardService.getMetrics(params),
            ]);

            console.log('[MarketingDashboard] Overview:', overviewData);
            console.log('[MarketingDashboard] Sources:', sourcesData);
            console.log('[MarketingDashboard] Timeline:', timelineData);
            console.log('[MarketingDashboard] Funnel:', funnelData);
            console.log('[MarketingDashboard] Metrics:', metricsData);

            setOverview(overviewData);
            setSources(sourcesData);
            setTimeline(processTimelineData(timelineData));
            setFunnel(funnelData);
            setMetrics(metricsData);
        } catch (error) {
            console.error("[MarketingDashboard] Error fetching dashboard data:", error);
            console.error("[MarketingDashboard] Error details:", error.response?.data || error.message);
        } finally {
            setLoading(false);
        }
    };

    const processTimelineData = (data) => {
        // Group by date
        const grouped = {};
        data.forEach(item => {
            if (!grouped[item.date]) {
                grouped[item.date] = { date: item.date };
            }
            grouped[item.date][item.source] = item.count;
        });
        return Object.values(grouped);
    };

    const handlePeriodChange = (e) => {
        setPeriod(e.target.value);
    };

    if (loading) {
        return (
            <MainContainer>
                <MainHeader>
                    <Title>Dashboard de Marketing</Title>
                </MainHeader>
                <Paper className={classes.mainPaper}>
                    <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                        <CircularProgress />
                    </Box>
                </Paper>
            </MainContainer>
        );
    }

    return (
        <MainContainer>
            <MainHeader>
                <Title>Dashboard de Marketing</Title>
            </MainHeader>

            <Paper className={classes.mainPaper}>
                {/* Filters */}
                <Box className={classes.filterBox}>
                    <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={4}>
                            <FormControl variant="outlined" fullWidth size="small">
                                <InputLabel>Período</InputLabel>
                                <Select
                                    value={period}
                                    onChange={handlePeriodChange}
                                    label="Período"
                                >
                                    <MenuItem value="7">Últimos 7 dias</MenuItem>
                                    <MenuItem value="30">Últimos 30 dias</MenuItem>
                                    <MenuItem value="90">Últimos 90 dias</MenuItem>
                                    <MenuItem value="custom">Personalizado</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        {period === "custom" && (
                            <>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Data Início"
                                        type="date"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <TextField
                                        label="Data Fim"
                                        type="date"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        InputLabelProps={{ shrink: true }}
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                    />
                                </Grid>
                            </>
                        )}
                    </Grid>
                </Box>

                {/* Dashboard Content */}
                <Grid container spacing={3}>
                    {/* Row 1 */}
                    <Grid item xs={12} md={6}>
                        {/* Leads Overview */}
                        <Card className={classes.card}>
                            <CardContent className={classes.cardContent}>
                                <Typography variant="h6" gutterBottom>
                                    Visão Geral das Conversas
                                </Typography>

                                <Box className={classes.metricCard} mb={2}>
                                    <Typography className={classes.metricValue}>
                                        {overview?.total || 0}
                                    </Typography>
                                    <Typography className={classes.metricLabel}>
                                        Total de Conversas Novas Ativas
                                    </Typography>
                                </Box>

                                {overview && (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <PieChart>
                                            <Pie
                                                data={[
                                                    { name: 'Rastreadas', value: overview.tracked },
                                                    { name: 'Não Rastreadas', value: overview.untracked }
                                                ]}
                                                cx="50%"
                                                cy="50%"
                                                innerRadius={60}
                                                outerRadius={80}
                                                fill="#8884d8"
                                                paddingAngle={5}
                                                dataKey="value"
                                                label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                                            >
                                                <Cell fill="#4CAF50" />
                                                <Cell fill="#FF9800" />
                                            </Pie>
                                            <Tooltip />
                                        </PieChart>
                                    </ResponsiveContainer>
                                )}

                                <Grid container spacing={2} style={{ marginTop: 16 }}>
                                    <Grid item xs={6}>
                                        <Box className={classes.metricCard}>
                                            <Typography variant="h6">{overview?.tracked || 0}</Typography>
                                            <Typography variant="caption">
                                                Rastreadas ({overview?.trackedPercentage || 0}%)
                                            </Typography>
                                        </Box>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Box className={classes.metricCard}>
                                            <Typography variant="h6">{overview?.untracked || 0}</Typography>
                                            <Typography variant="caption">
                                                Não Rastreadas ({overview?.untrackedPercentage || 0}%)
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        {/* Leads by Source */}
                        <Card className={classes.card}>
                            <CardContent className={classes.cardContent}>
                                <Typography variant="h6" gutterBottom>
                                    Origem das Conversas
                                </Typography>

                                <Grid container spacing={2} style={{ marginBottom: 16 }}>
                                    {sources.map((source) => (
                                        <Grid item xs={6} sm={3} key={source.source}>
                                            <Box className={classes.metricCard}>
                                                <Typography
                                                    variant="h5"
                                                    style={{ color: COLORS[source.source] || '#666' }}
                                                >
                                                    {source.count}
                                                </Typography>
                                                <Typography variant="caption">
                                                    {source.source}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    ))}
                                </Grid>

                                {timeline.length > 0 && (
                                    <ResponsiveContainer width="100%" height={250}>
                                        <BarChart data={timeline}>
                                            <CartesianGrid strokeDasharray="3 3" />
                                            <XAxis dataKey="date" />
                                            <YAxis />
                                            <Tooltip />
                                            <Legend />
                                            {Object.keys(COLORS).map((source) => (
                                                <Bar
                                                    key={source}
                                                    dataKey={source}
                                                    stackId="a"
                                                    fill={COLORS[source]}
                                                />
                                            ))}
                                        </BarChart>
                                    </ResponsiveContainer>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    {/* Row 2 */}
                    <Grid item xs={12} md={6}>
                        {/* Sales Funnel */}
                        <Card className={classes.card}>
                            <CardContent className={classes.cardContent}>
                                <Typography variant="h6" gutterBottom>
                                    Funil da Jornada de Compra
                                </Typography>

                                {funnel && (
                                    <Box mt={2}>
                                        <Box
                                            className={classes.funnelStep}
                                            style={{ width: '100%', backgroundColor: '#2196F3' }}
                                        >
                                            <Typography variant="h6">
                                                Fez Contato
                                            </Typography>
                                            <Typography variant="body2">
                                                {funnel.totalContacts} ({funnel.contactsPercentage}%)
                                            </Typography>
                                        </Box>

                                        <Box
                                            className={classes.funnelStep}
                                            style={{ width: '80%', margin: '16px auto', backgroundColor: '#1976D2' }}
                                        >
                                            <Typography variant="h6">
                                                Cotação de Valores
                                            </Typography>
                                            <Typography variant="body2">
                                                {funnel.totalQuotes} ({funnel.quotesPercentage}%)
                                            </Typography>
                                        </Box>

                                        <Box
                                            className={classes.funnelStep}
                                            style={{ width: '60%', margin: '16px auto', backgroundColor: '#1565C0' }}
                                        >
                                            <Typography variant="h6">
                                                Comprou
                                            </Typography>
                                            <Typography variant="body2">
                                                {funnel.totalSales} ({funnel.salesPercentage}%)
                                            </Typography>
                                        </Box>
                                    </Box>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>

                    <Grid item xs={12} md={6}>
                        {/* Sales Metrics */}
                        <Card className={classes.card}>
                            <CardContent className={classes.cardContent}>
                                <Typography variant="h6" gutterBottom>
                                    Vendas
                                </Typography>

                                {metrics && (
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} sm={4}>
                                            <Box className={classes.metricCard}>
                                                <Typography className={classes.metricValue}>
                                                    {metrics.totalSales}
                                                </Typography>
                                                <Typography className={classes.metricLabel}>
                                                    Total de Vendas
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box className={classes.metricCard}>
                                                <Typography className={classes.metricValue}>
                                                    {metrics.conversionRate}%
                                                </Typography>
                                                <Typography className={classes.metricLabel}>
                                                    Conversão (%)
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid item xs={12} sm={4}>
                                            <Box className={classes.metricCard}>
                                                <Typography className={classes.metricValue}>
                                                    R$ {metrics.totalRevenue.toFixed(2)}
                                                </Typography>
                                                <Typography className={classes.metricLabel}>
                                                    Faturamento Total
                                                </Typography>
                                            </Box>
                                        </Grid>
                                    </Grid>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Paper>
        </MainContainer>
    );
};

export default MarketingDashboard;
